import { FC, useState } from 'react';
import {
  VStack,
  HStack,
  Button,
  Input,
  FormControl,
  FormLabel,
  FormErrorMessage,
  useToast,
  Icon,
  Textarea,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Text
} from '@chakra-ui/react';
import { MdArrowBack, MdSave } from 'react-icons/md';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ADMIN_ROUTES } from '@bemap/utils';
import { Panel, PageHeading } from '@bemap/ui';
import { createContent, Content } from '@/services/university-service';
import { useForm } from 'react-hook-form';

interface DocumentContentFormData {
  title: string;
  content: string;
  order: number;
  duration: number;
}

const CreateDocumentContentPage: FC = () => {
  const navigate = useNavigate();
  const { courseId, moduleId } = useParams<{ courseId: string; moduleId: string }>();
  const toast = useToast();
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid }
  } = useForm<DocumentContentFormData>({
    defaultValues: {
      title: '',
      content: '',
      order: 1,
      duration: 15
    }
  });

  const handleBack = () => {
    navigate(`${ADMIN_ROUTES.UNIVERSITY_COURSES}/${courseId}`);
  };

  const onSubmit = async (data: DocumentContentFormData) => {
    setIsSubmitting(true);

    try {
      const contentData: Partial<Content> = {
        title: data.title,
        data: {
          markdown: data.content
        },
        type: 'document',
        order: data.order,
        duration: data.duration.toString(),
        moduleId: parseInt(moduleId!),
        completed: false
      };

      const response = await createContent(contentData);

      if (response.statusCode === 201 || response.statusCode === 200) {
        toast({
          title: t('university.content.document_created'),
          description: `${t('university.content.create_document')}"${data.title}" ${t('university.content.document_created')}`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });

        // Navigate back to the course
        navigate(`${ADMIN_ROUTES.UNIVERSITY_COURSES}/${courseId}`);
      } else {
        throw new Error(response.message || t('university.content.error_creating_document'));
      }
    } catch (error: any) {
      console.error('Error creating document content:', error);
      toast({
        title: t('university.content.error_creating_document'),
        description: error.message || t('general.error'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <VStack align="stretch" spacing={6}>
      {/* Header */}
      <PageHeading title={t('university.content.create_document')} actions={[
        {
          label: t('university.content.back_to_content_types'),
          onClick: handleBack,
          icon: <Icon as={MdArrowBack} />,
          variant: 'outline'
        }
      ]} />

      <form onSubmit={handleSubmit(onSubmit)}>
        <VStack align="stretch" spacing={6}>
          {/* Document Content Form */}
          <Panel heading={t('university.content.document_information')} padding={6}>
            <VStack spacing={4} align="stretch">
              <FormControl isInvalid={!!errors.title} isRequired>
                <FormLabel>{t('university.content.title')}</FormLabel>
                <Input
                  {...register('title', {
                    required: t('university.validation.title_required'),
                    minLength: { value: 3, message: t('university.validation.title_min_length') }
                  })}
                  placeholder={t('university.placeholders.document_title')}
                />
                <FormErrorMessage>{errors.title?.message}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.content} isRequired>
                <FormLabel>{t('university.content.content')}</FormLabel>
                <Textarea
                  {...register('content', {
                    required: t('university.validation.content_required'),
                    minLength: { value: 10, message: t('university.validation.content_min_length') }
                  })}
                  placeholder={t('university.placeholders.document_content')}
                  rows={12}
                  resize="vertical"
                />
                <FormErrorMessage>{errors.content?.message}</FormErrorMessage>
              </FormControl>

              <HStack spacing={4}>
                <FormControl>
                  <FormLabel>{t('university.content.order')}</FormLabel>
                  <NumberInput min={1} defaultValue={1}>
                    <NumberInputField {...register('order', { valueAsNumber: true })} />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>

                <FormControl>
                  <FormLabel>{t('university.content.estimated_duration')}</FormLabel>
                  <NumberInput min={1} defaultValue={15}>
                    <NumberInputField {...register('duration', { valueAsNumber: true })} />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                  <Text fontSize="sm" color="gray.600" mt={1}>
                    {t('university.content.minutes')}
                  </Text>
                </FormControl>
              </HStack>
            </VStack>
          </Panel>

          {/* Action Buttons */}
          <HStack justify="space-between">
            <Button
              variant="outline"
              onClick={handleBack}
              isDisabled={isSubmitting}
            >
              {t('actions.cancel')}
            </Button>

            <HStack spacing={3}>
              <Button
                type="submit"
                colorScheme="blue"
                leftIcon={<Icon as={MdSave} />}
                isLoading={isSubmitting}
                loadingText={t('general.creating')}
                isDisabled={!isValid}
              >
                {t('university.content.create_document')}
              </Button>
            </HStack>
          </HStack>
        </VStack>
      </form>
    </VStack>
  );
};

export default CreateDocumentContentPage; 